import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions, featuresActions } from '../../../store';
import { router } from '../../../App';
import TopMenu from '../../shared/TopMenu/TopMenu';
import styles from './StartPage.module.css';
import sqFeetIcon from './assets/sq-feet.jpg';
import homeIcon from './assets/home.jpg';
import bedroomIcon from './assets/bedroom.jpg';
import bathIcon from './assets/bath.jpg';

export default function StartPage() {
    const dispatch = useDispatch();
    const selectedFloorPlanData = useSelector(state => state.shared.selectedFloorPlanData);
    const [floorPlans, setFloorPlans] = useState([]);
    const [allFloorPlans, setAllFloorPlans] = useState([]); // Store all plans including interiors
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('');

    // Fetch floor plans from API
    useEffect(() => {
        const fetchFloorPlans = async () => {
            try {
                setLoading(true);
                const response = await fetch('https://lizard.chameleonpower.com/CDN/roomscenes/9759.json');
                if (!response.ok) {
                    throw new Error('Failed to fetch floor plans');
                }
                const data = await response.json();
                // Store all floor plans for finding matching interiors
                setAllFloorPlans(data);
                // Filter out "Home Interior" floor plans for display
                const filteredData = data.filter(plan => plan.productCategory !== 'Home Interior');
                setFloorPlans(filteredData);
                setError(null);
            } catch (err) {
                console.error('Error fetching floor plans:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchFloorPlans();
    }, []);

    // Find matching interior floor plan based on name prefix
    const findMatchingInterior = (exteriorPlan) => {
        // Extract the base name (e.g., "Byford" from "Byford Exterior")
        const baseName = exteriorPlan.name.replace(/\s*(Exterior|exterior)$/i, '').trim();
        
        // Find the matching interior plan
        return allFloorPlans.find(plan => 
            plan.productCategory === 'Home Interior' && 
            plan.name.toLowerCase().includes(baseName.toLowerCase())
        );
    };

    const handleFloorPlanSelect = (floorPlan) => {
        // Store the selected floor plan data in Redux state
        dispatch(appActions.setSelectedFloorPlanData(floorPlan));
        // Store the floor plan image URL for thumbnail use
        dispatch(featuresActions.setSelectedFloorPlanImageUrl(getImageUrl(floorPlan.specifier)));
        // Set the roomscene specifier for the visualizer
        dispatch(appActions.setRoomscene(floorPlan.specifier));
        dispatch(appActions.setCurrentView('room'));
        // Clear any selected surfaces
        dispatch(featuresActions.clearAllSelectedSurfaces());

        let specs = floorPlan.description.split(',');
        console.log(floorPlan);
        
        // Set dummy floor plan info (will be replaced with real data later)
        dispatch(featuresActions.setFloorPlanInfo({
            name: floorPlan.name.split(' ')[0],
            sqft: specs[0],
            beds: specs[1],
            bath: specs[2],
            garageCount: specs[3]
        }));
        
        // Set the exterior vizData with the selected floor plan specifier
        dispatch(featuresActions.setVizDataForTab({
            tab: 'exterior',
            specifier: floorPlan.specifier,
            sel: '-1'
        }));
        
        // Find and store the matching interior floor plan
        const matchingInterior = findMatchingInterior(floorPlan);
        if (matchingInterior) {
            dispatch(featuresActions.setSelectedInteriorFloorPlan(matchingInterior));
            // Set the kitchen vizData with the interior specifier
            dispatch(featuresActions.setVizDataForTab({
                tab: 'kitchen',
                specifier: matchingInterior.specifier,
                sel: '-1'
            }));
            console.log('Matching interior found:', matchingInterior);
        } else {
            dispatch(featuresActions.clearSelectedInteriorFloorPlan());
            console.log('No matching interior found for:', floorPlan.name);
        }
        
        console.log('Floor plan selected:', floorPlan);
        
        // Navigate to visualizer with specifier in URL
        router.navigate({
            pathname: `/visualizer/spec/${encodeURIComponent(floorPlan.specifier)}/sel/-1`,
            search: window.location.search,
            hash: window.location.hash,
        });
    };

    // Generate image URL from specifier
    const getImageUrl = (specifier) => {
        return `https://renderfarm1.chameleonpower.com/cham/GetImage.ashx?spec=${encodeURIComponent(specifier)}&sel=-1&type=base&size=600`;
    };

    // Helper to parse floor plan specs from description
    const parseSpecs = (floorPlan) => {
        const specs = floorPlan.description ? floorPlan.description.split(',') : [];
        return {
            sqft: parseFloat(specs[0]) || 0,
            beds: parseFloat(specs[1]) || 0,
            bath: parseFloat(specs[2]) || 0,
            garageCount: parseFloat(specs[3]) || 0
        };
    };

    // Sort floor plans
    const sortedFloorPlans = [...floorPlans].sort((a, b) => {
        const specsA = parseSpecs(a);
        const specsB = parseSpecs(b);
        
        switch (sortBy) {
            case 'A-Z':
                return a.name.localeCompare(b.name);
            case 'Z-A':
                return b.name.localeCompare(a.name);
            case 'Sq. Ft.: Low to High':
                return specsA.sqft - specsB.sqft;
            case 'Sq. Ft.: High to Low':
                return specsB.sqft - specsA.sqft;
            case 'Bedrooms: Low to High':
                return specsA.beds - specsB.beds;
            case 'Bedrooms: High to Low':
                return specsB.beds - specsA.beds;
            default:
                return 0;
        }
    });

    // Don't render if floor plan is already selected (navigating away)
    // if (selectedFloorPlanData) return null;

    return (
        <div className={styles.startPage}>
            <TopMenu variant="minimal" />
            <div className={styles.header}>
                <h1 className={styles.title}>Explore our Floor Plans</h1>
            </div>
            
            <div className={styles.sortWrapper}>
                <label className={styles.sortLabel}>
                    Sort By:
                    <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className={styles.sortSelect}
                    >
                        <option value="">Sort By:</option>
                        <option value="A-Z">A-Z</option>
                        <option value="Z-A">Z-A</option>
                        <option value="Sq. Ft.: Low to High">Sq. Ft.: Low to High</option>
                        <option value="Sq. Ft.: High to Low">Sq. Ft.: High to Low</option>
                        <option value="Bedrooms: Low to High">Bedrooms: Low to High</option>
                        <option value="Bedrooms: High to Low">Bedrooms: High to Low</option>
                    </select>
                </label>
            </div>

            <div className={styles.floorPlanGrid}>
                {loading && (
                    <div className={styles.loadingMessage}>Loading floor plans...</div>
                )}
                
                {error && (
                    <div className={styles.errorMessage}>Error: {error}</div>
                )}
                
                {!loading && !error && sortedFloorPlans.map((floorPlan) => {
                    // Parse description: "sqft,beds,bath,garageCount"
                    const specs = floorPlan.description ? floorPlan.description.split(',') : [];
                    const sqft = specs[0] || '--';
                    const beds = specs[1] || '--';
                    const bath = specs[2] || '--';
                    const garageCount = specs[3] || '--';
                    
                    return (
                        <div key={floorPlan.productId} className={styles.floorPlanCard}>
                            <div className={styles.imageWrapper}>
                                <img 
                                    src={getImageUrl(floorPlan.specifier)} 
                                    alt={floorPlan.name}
                                    className={styles.floorPlanImage}
                                    loading="lazy"
                                />
                            </div>
                            <div className={styles.cardContent}>
                                <h3 className={styles.floorPlanName}>{floorPlan.name}</h3>
                                <div className={styles.floorPlanDetails}>
                                    <span className={styles.detailItem}>
                                        <img className={styles.detailIcon} src={sqFeetIcon} alt="Square feet" />
                                        {sqft} Sq. Ft.
                                    </span>
                                    <span className={styles.detailItem}>
                                        <img className={styles.detailIcon} src={bedroomIcon} alt="Bedrooms" />
                                        {beds} Bedrooms
                                    </span>
                                    <div className={styles.detailItem}>
                                        <img className={styles.detailIcon} src={bathIcon} alt="Bathrooms" />
                                        {bath} Bathrooms
                                    </div>
                                </div>
                                
                                <span className={styles.detailItem}>
                                    <img className={styles.detailIcon} src={homeIcon} alt="Home" />
                                    {garageCount}-Car Attached Garage
                                </span>
                                <button 
                                    className={styles.selectButton}
                                    onClick={() => handleFloorPlanSelect(floorPlan)}
                                >
                                    Select Floor Plan
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
